import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import resourceService from "../../services/ResourceService";

import { Resource } from "../../interfaces";

interface Resources {
  resources: Resource[];
  singleResource?: Resource;
  error?: string | null;
  loading: boolean;
}

const initialState: Resources = {
  resources: [],
  singleResource: {} as Resource,
  error: "",
  loading: false,
};

export const fetchResources = createAsyncThunk(
  "resources/fetchResources",
  async (_, { rejectWithValue }) => {
    try {
      const response = await resourceService.getAll();
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const fetchResourceById = createAsyncThunk(
  "resources/fetchResourceById",
  async (id: number) => {
    try {
      const response = await resourceService.getById(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const addResource = createAsyncThunk(
  "resources/addResource",
  async (data: any) => {
    try {
      const response = await resourceService.post(data);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const updateResource = createAsyncThunk(
  "resources/updateResource",
  async (data: any) => {
    try {
      const response = await resourceService.put(data.id, {
        title: data.title,
        ext_agent_id: data.ext_agent_id,
        meta_data: data.meta_data,
        status: data.status,
        order: data.order,
        type: data.type,
      });
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const removeResource = createAsyncThunk(
  "resources/removeResource",
  async (id: number) => {
    try {
      await resourceService.remove(id);
      return id;
    } catch (error) {
      return error;
    }
  }
);

export const fetchResourcesByAgentId = createAsyncThunk(
  "resources/fetchResourcesByAgentId",
  async (id: number) => {
    try {
      const response = await resourceService.getByAgentId(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

const resourceSlice = createSlice({
  name: "resources",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchResources.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchResources.fulfilled, (state, action) => {
      state.resources = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchResources.rejected, (state, action) => {
      state.error = action.payload as string;
      state.loading = false;
    });

    builder.addCase(fetchResourceById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchResourceById.fulfilled, (state, action) => {
      state.singleResource = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchResourceById.rejected, (state, action) => {
      state.error = action.payload as string;
      state.loading = false;
    });

    builder.addCase(addResource.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addResource.fulfilled, (state, action) => {
      state.resources.push(action.payload);
      state.loading = false;
    });
    builder.addCase(addResource.rejected, (state, action) => {
      state.error = action.payload as string;
      state.loading = false;
    });

    builder.addCase(updateResource.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateResource.fulfilled, (state, action) => {
      state.singleResource = action.payload;
      state.loading = false;
    });
    builder.addCase(updateResource.rejected, (state, action) => {
      state.error = action.payload as string;
      state.loading = false;
    });

    builder.addCase(removeResource.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(removeResource.fulfilled, (state, action) => {
      state.resources = state.resources.filter(
        (resource) => resource.id !== action.payload
      );
      state.loading = false;
    });
    builder.addCase(removeResource.rejected, (state, action) => {
      state.error = action.payload as string;
      state.loading = false;
    });
  },
});

const { reducer } = resourceSlice;
export default reducer;
